// DTE (docs/108 B11). El motor PROPIO es el único destino: aquí se ve si está listo para folios
// («MOTOR LISTO · CAF/FOLIOS PENDIENTES» cuando falta el CAF del SII), se cargan y activan los CAF
// (la llave la guarda la custodia: el ERP nunca la muestra), se consultan los documentos y su estado,
// se reimprime y se revisan las incidencias. Producción sigue BLOQUEADA (readiness); en UAT nada va
// al SII.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, guardarArchivo, pedirArchivo } from '../api/cliente';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fecha, fechaHora, pesos } from '../comun/formato';
import { hoy } from '../compras/comun';
import { useSesion } from '../sesion/SesionContexto';
import { esPreproduccion, useSalud } from '../shell/salud';

const TIPOS: Record<number, string> = {
  33: 'Factura',
  34: 'Factura exenta',
  39: 'Boleta',
  41: 'Boleta exenta',
  52: 'Guía de despacho',
  56: 'Nota de débito',
  61: 'Nota de crédito',
};
const nombreTipo = (t: number) => TIPOS[t] ?? `Tipo ${t}`;

export interface DocumentoDte {
  id: string;
  tipo_dte: number;
  folio: number | null;
  estado: string;
  estado_envio: string;
  estado_sii: string;
  monto_total: string | null;
  fecha_emision: string | null;
  origen_id: string;
  creado_en: string;
  receptor_rut: string | null;
  receptor_razon_social: string | null;
  /** 0117: el último envío del XML al cliente. */
  intercambio_estado?: string | null;
  intercambio_para?: string | null;
  intercambio_en?: string | null;
}

/** 0117: los tipos cuyo XML va al correo de intercambio del cliente (la boleta no). */
export const TIPOS_INTERCAMBIO = [33, 34, 52, 56, 61];
const ROTULO_INTERCAMBIO: Record<string, string> = {
  ENVIADO: 'enviado',
  SOLICITADO: 'por enviar',
  FALLIDO: 'falló',
  SIN_CORREO: 'sin correo',
  NO_APLICA: 'no aplica',
};
const aceptadoSii = (d: DocumentoDte) =>
  ['ACEPTADO', 'ACEPTADO_CON_REPAROS'].includes(d.estado_sii);
/** Lo que se ve en la columna «XML al cliente». */
function textoIntercambio(d: DocumentoDte): string {
  if (!TIPOS_INTERCAMBIO.includes(d.tipo_dte)) return '';
  if (d.intercambio_estado) return ROTULO_INTERCAMBIO[d.intercambio_estado] ?? d.intercambio_estado;
  return d.estado === 'EMITIDO' && aceptadoSii(d) ? 'por enviar' : '—';
}

interface EstadoMotor {
  estado: 'MOTOR_NO_LISTO' | 'MOTOR_LISTO_CAF_PENDIENTE' | 'LISTO_PARCIAL' | 'LISTO_CON_FOLIOS';
  motorListoParaFolios: boolean;
  ambiente: string | null;
  capacidad: string | null;
  certificado: { huella: string; vigenteHasta: string } | null;
  faltantes: string[];
  porTipo: Array<{
    tipoDte: number;
    cafActivos: number;
    porActivar: number;
    foliosDisponibles: number;
  }>;
}

interface Caf {
  id: string;
  tipo_dte: number;
  folio_desde: number;
  folio_hasta: number;
  fecha_autorizacion: string;
  vence_en: string | null;
  custodio: string;
  estado: string;
  version: number;
  siguiente: number | null;
  ambiente: string | null;
}

function useAccionDte(empresaId: string) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  return useMutation<unknown, Error, { ruta: string; cuerpo: unknown }>({
    mutationFn: ({ ruta, cuerpo }) =>
      api.post(ruta, cuerpo, { idempotencia: intentos.clave(`dte:${ruta}`, cuerpo) }),
    onSuccess: async () => {
      intentos.cerrar();
      await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'dte-admin'] });
    },
    onError: (e) => intentos.cerrar(e),
  });
}

const ROTULO_MOTOR: Record<EstadoMotor['estado'], string> = {
  MOTOR_NO_LISTO: 'MOTOR NO LISTO',
  MOTOR_LISTO_CAF_PENDIENTE: 'MOTOR LISTO · CAF/FOLIOS PENDIENTES',
  LISTO_PARCIAL: 'MOTOR LISTO · FOLIOS EN ALGUNOS TIPOS',
  LISTO_CON_FOLIOS: 'MOTOR LISTO CON FOLIOS',
};
/**
 * PREPRODUCCIÓN (no oficial): el código del motor propio está en la imagen, pero la instancia no lo
 * conecta (sin custodia ni emisión), no hay CAF y la certificación final H5 no está cerrada. Decirlo
 * así, sin «MOTOR LISTO». Solo rótulo: el estado real sigue en `data-estado-motor`.
 */
const ROTULO_PREPRODUCCION =
  'MOTOR INSTALADO · CAF/FOLIOS PENDIENTES · CERTIFICACIÓN FINAL H5 PENDIENTE';

const FALTA: Record<string, string> = {
  MOTOR_NO_CONECTADO: 'El motor propio no está conectado en esta instancia.',
  EMISOR_NO_CONFIGURADO: 'La empresa no tiene emisor DTE configurado.',
  CERTIFICADO_NO_DISPONIBLE: 'No hay certificado digital activo y vigente en la custodia.',
};

// ================================================================ monitor

export function MonitorDte() {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const motor = useQuery({
    queryKey: ['emp', empresaId, 'dte-admin', 'motor'],
    queryFn: () => api.get<EstadoMotor>('/dte/motor/estado', { query: { empresaId } }),
  });
  const readiness = useQuery({
    queryKey: ['emp', empresaId, 'dte-admin', 'readiness'],
    queryFn: () =>
      api.get<{
        produccion: string;
        bloqueos: Array<{ codigo: string; decision: string; detalle: string }>;
      }>('/dte/readiness', { query: { empresaId } }),
  });
  const m = motor.data;
  const preproduccion = esPreproduccion(useSalud().data);
  return (
    <div className="pantalla consulta" data-pantalla="monitor-dte">
      {motor.isError && <PanelError error={motor.error} />}
      {m && (
        <div
          className={m.motorListoParaFolios ? 'panel-revision' : 'sin-fuente'}
          role="status"
          aria-label="Estado del motor DTE"
          data-estado-motor={m.estado}
        >
          <b>{preproduccion ? ROTULO_PREPRODUCCION : ROTULO_MOTOR[m.estado]}</b>
          <span>
            {' '}
            · motor propio · ambiente {m.ambiente?.toLowerCase() ?? '—'}
            {preproduccion ? ' · PREPRODUCCIÓN: sin emisión DTE ni envío al SII' : ''}
            {m.certificado
              ? ` · certificado vigente hasta ${fecha(m.certificado.vigenteHasta)}`
              : ''}
          </span>
          {m.faltantes.length > 0 && (
            <ul>
              {m.faltantes.map((f) => (
                <li key={f}>{FALTA[f] ?? f}</li>
              ))}
            </ul>
          )}
        </div>
      )}
      {m && (
        <table className="grilla compacta" aria-label="Folios por tipo">
          <thead>
            <tr>
              <th>Tipo</th>
              <th className="der">CAF activos</th>
              <th className="der">CAF por activar</th>
              <th className="der">Folios disponibles</th>
            </tr>
          </thead>
          <tbody>
            {m.porTipo.map((t) => (
              <tr key={t.tipoDte}>
                <td>
                  {t.tipoDte} · {nombreTipo(t.tipoDte)}
                </td>
                <td className="der">{t.cafActivos}</td>
                <td className="der">{t.porActivar}</td>
                <td className="der">
                  {t.foliosDisponibles ? (
                    <b>{t.foliosDisponibles}</b>
                  ) : (
                    <span className="tono-malo">sin folios</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <h3 className="titulo-seccion">Producción: {readiness.data?.produccion ?? '…'}</h3>
      <p className="apagado">
        Lo que hoy impide emitir en producción (se levanta con decisión de Rodrigo, el contador o
        una migración; ninguna se levanta sola).
      </p>
      {readiness.isError && <PanelError error={readiness.error} />}
      <table className="grilla compacta" aria-label="Bloqueos de producción">
        <thead>
          <tr>
            <th>Bloqueo</th>
            <th>Decisión</th>
            <th>Detalle</th>
          </tr>
        </thead>
        <tbody>
          {(readiness.data?.bloqueos ?? []).map((b) => (
            <tr key={b.codigo}>
              <td className="mono">{b.codigo}</td>
              <td>{b.decision}</td>
              <td>{b.detalle}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ================================================================ CAF y folios

export function FoliosCaf() {
  const preproduccion = esPreproduccion(useSalud().data);
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const accion = useAccionDte(empresaId);
  const cafs = useQuery({
    queryKey: ['emp', empresaId, 'dte-admin', 'caf'],
    queryFn: () => api.get<Caf[]>('/dte/caf', { query: { empresaId } }),
  });
  const folios = useQuery({
    queryKey: ['emp', empresaId, 'dte-admin', 'folios'],
    queryFn: () =>
      api.get<{
        asignados: Array<{
          tipo_dte: number;
          folio: number;
          estado: string;
          estado_sii: string;
          registrado_en: string;
        }>;
        anulados: Array<{ tipo_dte: number; folio: number; motivo: string }>;
      }>('/dte/folios', { query: { empresaId } }),
  });
  const [cargar, setCargar] = useState(false);
  const [cambio, setCambio] = useState<{
    caf: Caf;
    estado: 'ACTIVO' | 'BLOQUEADO' | 'RETIRADO';
  } | null>(null);
  const [anular, setAnular] = useState(false);
  const administra = puede('dte.caf.administrar');
  // Activar / reactivar es directo; queda quién y cuándo (sin pedir motivo).
  const cambiarEstado = (caf: Caf, estado: 'ACTIVO' | 'BLOQUEADO' | 'RETIRADO') =>
    accion.mutate(
      {
        ruta: `/dte/caf/${caf.id}/estado-nativo`,
        cuerpo: { estado, version: caf.version },
      },
      { onSuccess: () => setCambio(null) },
    );
  const motor = useQuery({
    queryKey: ['emp', empresaId, 'dte-admin', 'motor'],
    queryFn: () => api.get<EstadoMotor>('/dte/motor/estado', { query: { empresaId } }),
  });
  return (
    <div className="pantalla consulta motor-propio" data-pantalla="folios-caf">
      <header className="encabezado-entidad">
        <span className="ruta-migas">DTE › Motor propio</span>
        <h2>Certificado y folios</h2>
      </header>
      <p className="apagado">
        Para emitir se necesita el certificado digital vigente y folios (CAF) activos para cada tipo
        de documento.
      </p>
      <CertificadoDigital />
      <fieldset className="recuadro-ficha" aria-label="Folios (CAF)">
        <legend>2 · Folios autorizados por el SII (CAF)</legend>
        {motor.data && (
          <div className="resumen-folios">
            {motor.data.porTipo.map((t) => (
              <span
                key={t.tipoDte}
                className={`cifra ${t.foliosDisponibles ? '' : 'mala'}`}
                title={`${t.cafActivos} CAF activos · ${t.porActivar} por activar`}
              >
                <span className="etiqueta">{nombreTipo(t.tipoDte)}</span>
                <b>{t.foliosDisponibles ? t.foliosDisponibles : 'sin folios'}</b>
              </span>
            ))}
          </div>
        )}
        <p className="apagado">
          El CAF se descarga del SII (Timbraje electrónico › Solicitar timbraje), se carga aquí y se
          usa al pulsar <b>Activar</b>.
        </p>
        <div className="barra-filtros">
          <span className="espaciador" />
          {administra && (
            <>
              <button type="button" className="primario" onClick={() => setCargar(true)}>
                Cargar CAF
              </button>
              <button type="button" onClick={() => setAnular(true)}>
                Anotar folio anulado en el SII
              </button>
            </>
          )}
        </div>
        {cafs.isError && <PanelError error={cafs.error} />}
        {accion.isError && !cambio && !cargar && <PanelError error={accion.error} />}
        <table className="grilla compacta" aria-label="CAF">
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Rango</th>
              <th className="der">Siguiente</th>
              <th>Autorizado</th>
              <th>Vence</th>
              <th>Motor</th>
              <th>Estado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(cafs.data ?? []).map((c) => (
              <tr key={c.id}>
                <td>
                  {c.tipo_dte} · {nombreTipo(c.tipo_dte)}
                </td>
                <td className="mono">
                  {c.folio_desde}–{c.folio_hasta}
                </td>
                <td className="der">{c.siguiente ?? ''}</td>
                <td>{fecha(c.fecha_autorizacion)}</td>
                <td>{c.vence_en ? fecha(c.vence_en) : 'no vence'}</td>
                <td>{c.custodio === 'NATIVO' ? 'propio' : c.custodio.toLowerCase()}</td>
                <td>{c.estado.toLowerCase()}</td>
                <td>
                  {administra && c.custodio === 'NATIVO' && (
                    <span className="acciones-fila">
                      {(c.estado === 'VALIDADO' ||
                        c.estado === 'BLOQUEADO' ||
                        (c.estado === 'RETIRADO' && c.siguiente === c.folio_desde)) && (
                        <button
                          type="button"
                          className="boton-mini primario"
                          disabled={accion.isPending}
                          onClick={() => cambiarEstado(c, 'ACTIVO')}
                        >
                          {c.estado === 'RETIRADO' ? 'Reactivar' : 'Activar'}
                        </button>
                      )}
                      {c.estado === 'ACTIVO' && (
                        <button
                          type="button"
                          className="boton-mini"
                          onClick={() => setCambio({ caf: c, estado: 'BLOQUEADO' })}
                        >
                          Bloquear
                        </button>
                      )}
                      {c.estado !== 'RETIRADO' && (
                        <button
                          type="button"
                          className="boton-mini"
                          onClick={() => setCambio({ caf: c, estado: 'RETIRADO' })}
                        >
                          Retirar
                        </button>
                      )}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {!cafs.data?.length && (
              <tr>
                <td colSpan={8} className="grilla-vacia">
                  {cafs.isLoading
                    ? 'Cargando…'
                    : preproduccion
                      ? `Sin CAF cargados: ${ROTULO_PREPRODUCCION}`
                      : 'Sin CAF cargados: MOTOR LISTO · CAF/FOLIOS PENDIENTES'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </fieldset>
      <fieldset className="recuadro-ficha" aria-label="Folios usados">
        <legend>3 · Folios usados</legend>
        <table className="grilla compacta" aria-label="Folios usados">
          <thead>
            <tr>
              <th>Tipo</th>
              <th className="der">Folio</th>
              <th>Documento</th>
              <th>SII</th>
              <th>Asignado</th>
            </tr>
          </thead>
          <tbody>
            {(folios.data?.asignados ?? []).slice(-200).map((f) => (
              <tr key={`${f.tipo_dte}-${f.folio}`}>
                <td>{nombreTipo(f.tipo_dte)}</td>
                <td className="der mono">{f.folio}</td>
                <td>{f.estado.toLowerCase()}</td>
                <td>{f.estado_sii.toLowerCase()}</td>
                <td>{fechaHora(f.registrado_en)}</td>
              </tr>
            ))}
            {(folios.data?.anulados ?? []).map((f) => (
              <tr key={`a-${f.tipo_dte}-${f.folio}`}>
                <td>{nombreTipo(f.tipo_dte)}</td>
                <td className="der mono">{f.folio}</td>
                <td colSpan={3}>anulado sin uso · {f.motivo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </fieldset>
      {cargar && <CargarCaf alCerrar={() => setCargar(false)} />}
      {cambio && (
        <Modal
          titulo={`${cambio.estado === 'BLOQUEADO' ? 'Bloquear' : 'Retirar'} CAF ${cambio.caf.tipo_dte} ${cambio.caf.folio_desde}–${cambio.caf.folio_hasta}`}
          alCerrar={() => setCambio(null)}
          ancho="angosto"
        >
          <p>
            {cambio.estado === 'BLOQUEADO'
              ? 'El CAF deja de entregar folios hasta que lo vuelvas a activar.'
              : 'El CAF deja de entregar folios. Si no se usó ningún folio, se puede reactivar.'}
          </p>
          {accion.isError && <PanelError error={accion.error} />}
          <div className="acciones-modal">
            <button type="button" onClick={() => setCambio(null)}>
              Volver
            </button>
            <button
              type="button"
              className="primario"
              disabled={accion.isPending}
              onClick={() => cambiarEstado(cambio.caf, cambio.estado)}
            >
              Confirmar
            </button>
          </div>
        </Modal>
      )}
      {anular && <AnularFolio alCerrar={() => setAnular(false)} />}
    </div>
  );
}

interface Certificado {
  id: string;
  estado: string;
  huella: string;
  serie: string | null;
  sujeto: string;
  emisor: string | null;
  rut_titular: string | null;
  vigente_desde: string;
  vigente_hasta: string;
  activado_en: string | null;
}

function CertificadoDigital() {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const accion = useAccionDte(empresaId);
  const [cargar, setCargar] = useState(false);
  const q = useQuery({
    queryKey: ['emp', empresaId, 'dte-admin', 'certificados'],
    queryFn: () =>
      api.get<{ configurada: boolean; certificados: Certificado[] }>('/dte/certificados', {
        query: { empresaId },
      }),
  });
  const administra = puede('dte.certificado.administrar');
  const lista = q.data?.certificados ?? [];
  const activo = lista.find((c) => c.estado === 'ACTIVO') ?? null;
  const dias = activo
    ? Math.floor((new Date(activo.vigente_hasta).getTime() - Date.now()) / 86_400_000)
    : null;
  return (
    <fieldset className="recuadro-ficha" aria-label="Certificado digital">
      <legend>1 · Certificado digital (firma electrónica)</legend>
      {q.isError && <PanelError error={q.error} />}
      {q.data && !q.data.configurada && (
        <p className="tono-aviso">
          La custodia cifrada del motor propio no está configurada en este servidor: el certificado
          todavía no se puede cargar aquí.
        </p>
      )}
      <div className="barra-filtros">
        <span
          className={activo ? 'tono-bueno' : 'tono-malo'}
          data-certificado={activo ? 'activo' : 'falta'}
        >
          {activo ? (
            <>
              <b>Vence el {fecha(activo.vigente_hasta)}</b>
              {dias !== null && dias <= 30 ? ` (quedan ${dias} días: renuévalo)` : ''} · firma{' '}
              {nombreTitular(activo.sujeto)}
              {activo.rut_titular ? ` (RUT ${activo.rut_titular})` : ''}
            </>
          ) : (
            <b>Sin certificado activo: el motor propio no puede firmar.</b>
          )}
        </span>
        <span className="espaciador" />
        {administra && q.data?.configurada && (
          <button type="button" className="primario" onClick={() => setCargar(true)}>
            {activo ? 'Cargar certificado nuevo' : 'Cargar certificado'}
          </button>
        )}
      </div>
      {lista.length > 0 && (
        <details>
          <summary className="apagado">Ver detalle</summary>
          <table className="grilla compacta" aria-label="Certificados">
            <thead>
              <tr>
                <th>Titular</th>
                <th>RUT</th>
                <th>Emitido por</th>
                <th>Vigente</th>
                <th>Huella</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lista.map((c) => (
                <tr key={c.id}>
                  <td>{c.sujeto}</td>
                  <td className="mono">{c.rut_titular ?? '—'}</td>
                  <td>{c.emisor ?? '—'}</td>
                  <td>
                    {fecha(c.vigente_desde)} al {fecha(c.vigente_hasta)}
                  </td>
                  <td className="mono" title={c.huella}>
                    {c.huella.slice(0, 12)}…
                  </td>
                  <td>{c.estado.toLowerCase()}</td>
                  <td>
                    {administra && c.estado !== 'ACTIVO' && c.estado !== 'DESHABILITADO' && (
                      <button
                        type="button"
                        className="boton-mini"
                        disabled={accion.isPending}
                        onClick={() =>
                          accion.mutate({
                            ruta: `/dte/certificados/${c.id}/activar`,
                            cuerpo: { empresaId },
                          })
                        }
                      >
                        Activar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      {cargar && <CargarCertificado alCerrar={() => setCargar(false)} />}
    </fieldset>
  );
}

/** Solo el nombre (CN) del sujeto del certificado; el resto es detalle técnico. */
function nombreTitular(sujeto: string): string {
  return /CN=([^,]+)/.exec(sujeto)?.[1]?.trim() ?? sujeto;
}

/** Lee el archivo como base64 (sin el prefijo data:). */
function enBase64(f: File): Promise<string> {
  return new Promise((ok, mal) => {
    const r = new FileReader();
    r.onload = () => ok(String(r.result ?? '').replace(/^data:[^,]*,/, ''));
    r.onerror = () => mal(r.error ?? new Error('No se pudo leer el archivo'));
    r.readAsDataURL(f);
  });
}

function CargarCertificado({ alCerrar }: { alCerrar: () => void }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [clave, setClave] = useState('');
  // La clave NO entra en la llave de idempotencia ni se guarda: viaja una vez y se descarta.
  const cargar = useMutation({
    mutationFn: async () =>
      api.post<{ sujeto: string; vigenteHasta: string; activado: boolean; repetido: boolean }>(
        '/dte/certificados',
        {
          empresaId,
          archivo: await enBase64(archivo!),
          clave,
          activar: true,
        },
        {
          idempotencia: `cert:${empresaId}:${archivo!.name}:${archivo!.size}:${archivo!.lastModified}`,
        },
      ),
    onSuccess: async () => {
      setClave('');
      await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'dte-admin'] });
      alCerrar();
    },
    onError: () => setClave(''),
  });
  return (
    <Modal titulo="Cargar certificado digital" alCerrar={alCerrar}>
      <p className="apagado">
        El archivo <b>.pfx</b> o <b>.p12</b> del certificado del representante legal y su clave. La
        clave solo abre el archivo y se descarta (no se guarda en ninguna parte); la llave privada
        queda cifrada en la custodia. Se valida que esté vigente y que firme antes de activarlo.
      </p>
      <label className="campo">
        <span className="etiqueta">Archivo del certificado (.pfx / .p12)</span>
        <input
          type="file"
          aria-label="Archivo del certificado"
          accept=".pfx,.p12,application/x-pkcs12"
          onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
        />
      </label>
      <label className="campo">
        <span className="etiqueta">Clave del certificado</span>
        <input
          type="password"
          aria-label="Clave del certificado"
          autoComplete="off"
          value={clave}
          onChange={(e) => setClave(e.target.value)}
        />
      </label>
      {cargar.isError && <PanelError error={cargar.error} />}
      <div className="acciones-modal">
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button
          type="button"
          className="primario"
          disabled={!archivo || !clave || cargar.isPending}
          onClick={() => cargar.mutate()}
        >
          {cargar.isPending ? 'Cargando…' : 'Cargar y activar'}
        </button>
      </div>
    </Modal>
  );
}

function ConMotivo({
  titulo,
  accion,
  alConfirmar,
  alCerrar,
}: {
  titulo: string;
  accion: ReturnType<typeof useAccionDte>;
  alConfirmar: (m: string) => void;
  alCerrar: () => void;
}) {
  const [m, setM] = useState('');
  return (
    <Modal titulo={titulo} alCerrar={alCerrar} ancho="angosto">
      <label className="campo">
        <span className="etiqueta">Motivo</span>
        <input aria-label="Motivo" autoFocus value={m} onChange={(e) => setM(e.target.value)} />
      </label>
      {accion.isError && <PanelError error={accion.error} />}
      <div className="acciones-modal">
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button
          type="button"
          className="primario"
          disabled={m.trim().length < 3 || accion.isPending}
          onClick={() => alConfirmar(m.trim())}
        >
          Confirmar
        </button>
      </div>
    </Modal>
  );
}

function CargarCaf({ alCerrar }: { alCerrar: () => void }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const accion = useAccionDte(empresaId);
  const [archivo, setArchivo] = useState('');
  const [nombre, setNombre] = useState('');
  return (
    <Modal titulo="Cargar CAF del SII" alCerrar={alCerrar}>
      <p className="apagado">
        El archivo XML tal como lo descargaste del SII. Se valida la firma del SII, el RUT de la
        empresa, el tipo, el rango y la vigencia antes de guardar nada.
      </p>
      <label className="campo">
        <span className="etiqueta">Archivo CAF (.xml)</span>
        <input
          type="file"
          aria-label="Archivo CAF"
          accept=".xml,text/xml"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            setNombre(f.name);
            // El CAF del SII viene en ISO-8859-1: se lee como latin1 para no alterar la firma.
            const r = new FileReader();
            r.onload = () => setArchivo(String(r.result ?? ''));
            r.readAsText(f, 'ISO-8859-1');
          }}
        />
        {nombre && <span className="apagado">{nombre}</span>}
      </label>
      {accion.isError && <PanelError error={accion.error} />}
      <div className="acciones-modal">
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button
          type="button"
          className="primario"
          disabled={archivo.length < 50 || accion.isPending}
          onClick={() =>
            accion.mutate(
              {
                ruta: '/dte/caf/nativo',
                cuerpo: { empresaId, archivoCaf: archivo },
              },
              { onSuccess: alCerrar },
            )
          }
        >
          Cargar
        </button>
      </div>
    </Modal>
  );
}

function AnularFolio({ alCerrar }: { alCerrar: () => void }) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const accion = useAccionDte(empresaId);
  const [tipo, setTipo] = useState('33');
  const [folio, setFolio] = useState('');
  const [motivo, setMotivo] = useState('');
  return (
    <Modal titulo="Anotar folio anulado en el SII" alCerrar={alCerrar} ancho="angosto">
      <p className="apagado">
        Registra lo que la administración ya anuló en sii.cl (un folio sin documento). El ERP no
        habla con el SII para esto.
      </p>
      <label className="campo">
        <span className="etiqueta">Tipo</span>
        <select aria-label="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
          {Object.entries(TIPOS).map(([k, v]) => (
            <option key={k} value={k}>
              {k} · {v}
            </option>
          ))}
        </select>
      </label>
      <label className="campo">
        <span className="etiqueta">Folio</span>
        <input
          aria-label="Folio"
          inputMode="numeric"
          value={folio}
          onChange={(e) => setFolio(e.target.value)}
        />
      </label>
      <label className="campo">
        <span className="etiqueta">Motivo</span>
        <input aria-label="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </label>
      {accion.isError && <PanelError error={accion.error} />}
      <div className="acciones-modal">
        <button type="button" onClick={alCerrar}>
          Volver
        </button>
        <button
          type="button"
          className="primario"
          disabled={!/^\d+$/.test(folio) || motivo.trim().length < 3 || accion.isPending}
          onClick={() =>
            accion.mutate(
              {
                ruta: '/dte/folios/anular',
                cuerpo: {
                  empresaId,
                  tipoDte: Number(tipo),
                  folio: Number(folio),
                  motivo: motivo.trim(),
                },
              },
              { onSuccess: alCerrar },
            )
          }
        >
          Anotar
        </button>
      </div>
    </Modal>
  );
}

// ================================================================ documentos, estado SII y reimpresión

export function DocumentosDte({
  modo,
  tipoInicial,
  alAbrirDte,
}: {
  modo: 'documentos' | 'estado-sii' | 'reimpresion' | 'incidencias';
  tipoInicial?: string | undefined;
  alAbrirNota?: (notaId: string, numero: string) => void;
  /** La ficha del documento (2026-10-26): ahí está su origen, sus NC y la reimpresión. */
  alAbrirDte: (documentoId: string, titulo: string) => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const accion = useAccionDte(empresaId);
  const [desde, setDesde] = useState(`${hoy().slice(0, 8)}01`);
  const [hasta, setHasta] = useState(hoy());
  const [tipo, setTipo] = useState(tipoInicial ?? '');
  const [folio, setFolio] = useState('');
  const [estado, setEstado] = useState(modo === 'incidencias' ? 'FALLIDO' : '');
  const [reintentar, setReintentar] = useState<DocumentoDte | null>(null);
  const [xmlCliente, setXmlCliente] = useState<DocumentoDte | null>(null);
  const [error, setError] = useState<unknown>(null);
  const q = useQuery({
    queryKey: [
      'emp',
      empresaId,
      'dte-admin',
      'documentos',
      modo,
      desde,
      hasta,
      tipo,
      folio,
      estado,
    ],
    queryFn: () =>
      api.get<DocumentoDte[]>('/dte/documentos', {
        query: {
          empresaId,
          ...(folio ? { folio } : modo === 'incidencias' ? {} : { desde, hasta }),
          tipoDte: tipo || undefined,
          estado: estado || undefined,
          limite: 500,
        },
      }),
  });
  const recon = useQuery({
    queryKey: ['emp', empresaId, 'dte-admin', 'reconciliacion'],
    queryFn: () =>
      api.get<{
        resultado: string;
        hallazgos: Array<{ clase: string; detalle: Record<string, unknown> }>;
        huecos: unknown;
      }>('/dte/reconciliacion', { query: { empresaId } }),
    enabled: modo === 'incidencias' && puede('dte.folios.ver'),
  });
  const filas = (q.data ?? []).filter(
    (d) =>
      modo !== 'incidencias' ||
      ['FALLIDO', 'EN_CONFLICTO'].includes(d.estado) ||
      ['RECHAZADO', 'REPARO'].includes(d.estado_sii) ||
      ['FALLIDO', 'SIN_CORREO'].includes(d.intercambio_estado ?? ''),
  );
  const ver = async (d: DocumentoDte, que: 'pdf' | 'xml', ejemplar = 'ambos') => {
    setError(null);
    try {
      const b = await pedirArchivo(
        'GET',
        `/dte/documentos/${d.id}/${que}`,
        que === 'pdf' ? { query: { ejemplar } } : {},
      );
      if (que === 'pdf') window.open(URL.createObjectURL(b), '_blank', 'noopener');
      else guardarArchivo(b, `DTE${d.tipo_dte}-${d.folio ?? d.id.slice(0, 8)}.xml`);
    } catch (e) {
      setError(e);
    }
  };
  return (
    <div className="pantalla consulta" data-pantalla={`dte-${modo}`}>
      <div className="barra-filtros">
        {modo !== 'incidencias' && (
          <>
            <label className="campo">
              <span className="etiqueta">Desde</span>
              <input
                type="date"
                aria-label="Desde"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
              />
            </label>
            <label className="campo">
              <span className="etiqueta">Hasta</span>
              <input
                type="date"
                aria-label="Hasta"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
              />
            </label>
          </>
        )}
        <label className="campo">
          <span className="etiqueta">Tipo</span>
          <select aria-label="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(TIPOS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label className="campo campo-corto">
          <span className="etiqueta">Folio</span>
          <input
            aria-label="Folio"
            inputMode="numeric"
            value={folio}
            onChange={(e) => setFolio(e.target.value.replace(/\D/g, ''))}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Estado</span>
          <select
            aria-label="Estado del documento"
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
          >
            <option value="">Todos</option>
            {['SOLICITADO', 'EMITIDO', 'FALLIDO', 'ANULADO_SIN_EMISION', 'EN_CONFLICTO'].map(
              (x) => (
                <option key={x} value={x}>
                  {x.toLowerCase().replace(/_/g, ' ')}
                </option>
              ),
            )}
          </select>
        </label>
      </div>
      {q.isError || error ? <PanelError error={q.error ?? error} /> : null}
      <table className="grilla compacta" aria-label="Documentos tributarios">
        <thead>
          <tr>
            <th>Tipo</th>
            <th className="der">Folio</th>
            <th>Emisión</th>
            <th>Receptor</th>
            <th className="der">Total</th>
            <th>Documento</th>
            <th>Envío</th>
            <th>SII</th>
            <th>XML al cliente</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {filas.map((d) => (
            <tr
              key={d.id}
              onDoubleClick={() =>
                alAbrirDte(d.id, `${nombreTipo(d.tipo_dte)} N° ${d.folio ?? ''}`)
              }
            >
              <td>{nombreTipo(d.tipo_dte)}</td>
              <td className="der mono">{d.folio ?? '—'}</td>
              <td>{fecha(d.fecha_emision)}</td>
              <td>{d.receptor_razon_social ?? d.receptor_rut ?? ''}</td>
              <td className="der">{pesos(d.monto_total)}</td>
              <td>{d.estado.toLowerCase().replace(/_/g, ' ')}</td>
              <td>{d.estado_envio.toLowerCase().replace(/_/g, ' ')}</td>
              <td>{d.estado_sii.toLowerCase().replace(/_/g, ' ')}</td>
              <td
                data-intercambio={d.intercambio_estado ?? ''}
                title={
                  d.intercambio_para
                    ? `${d.intercambio_para} · ${fechaHora(d.intercambio_en ?? null)}`
                    : undefined
                }
              >
                {textoIntercambio(d)}
              </td>
              <td>
                <span className="acciones-fila">
                  {d.estado === 'EMITIDO' && (
                    <>
                      <button
                        type="button"
                        className="boton-mini"
                        onClick={() => void ver(d, 'pdf')}
                      >
                        PDF
                      </button>
                      {modo === 'reimpresion' && [33, 34].includes(d.tipo_dte) && (
                        <button
                          type="button"
                          className="boton-mini"
                          onClick={() => void ver(d, 'pdf', 'cedible')}
                        >
                          Cedible
                        </button>
                      )}
                      {modo !== 'reimpresion' && (
                        <button
                          type="button"
                          className="boton-mini"
                          onClick={() => void ver(d, 'xml')}
                        >
                          XML
                        </button>
                      )}
                    </>
                  )}
                  {d.estado === 'EMITIDO' &&
                    aceptadoSii(d) &&
                    TIPOS_INTERCAMBIO.includes(d.tipo_dte) && (
                      <button
                        type="button"
                        className="boton-mini"
                        title="Ver o reenviar el XML al correo de intercambio del cliente"
                        onClick={() => setXmlCliente(d)}
                      >
                        Enviar XML
                      </button>
                    )}
                  {d.estado === 'FALLIDO' && puede('dte.reintentar') && (
                    <button type="button" className="boton-mini" onClick={() => setReintentar(d)}>
                      Reintentar
                    </button>
                  )}
                  <button
                    type="button"
                    className="boton-mini"
                    title="La ficha del documento: detalle, origen y notas de crédito"
                    onClick={() =>
                      alAbrirDte(d.id, `${nombreTipo(d.tipo_dte)} N° ${d.folio ?? ''}`)
                    }
                  >
                    Abrir
                  </button>
                </span>
              </td>
            </tr>
          ))}
          {!filas.length && (
            <tr>
              <td colSpan={10} className="grilla-vacia">
                {q.isLoading
                  ? 'Cargando…'
                  : modo === 'incidencias'
                    ? 'Sin documentos con problema'
                    : 'Sin documentos'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {modo === 'incidencias' && recon.data && (
        <>
          <h3 className="titulo-seccion">
            Reconciliación de folios:{' '}
            {recon.data.resultado === 'CUADRA' ? 'cuadra' : 'con hallazgos'}
          </h3>
          <ul>
            {recon.data.hallazgos.map((h, i) => (
              <li key={i}>
                <span className="mono">{h.clase}</span> {JSON.stringify(h.detalle)}
              </li>
            ))}
          </ul>
        </>
      )}
      {xmlCliente && (
        <XmlAlCliente
          documento={xmlCliente}
          puedeEnviar={puede('dte.intercambio.enviar')}
          alCerrar={() => setXmlCliente(null)}
        />
      )}
      {reintentar && (
        <ConMotivo
          titulo={`Reintentar ${nombreTipo(reintentar.tipo_dte)} ${reintentar.folio ?? ''}`}
          accion={accion}
          alConfirmar={(motivo) =>
            accion.mutate(
              { ruta: `/dte/documentos/${reintentar.id}/reintentar`, cuerpo: { motivo } },
              { onSuccess: () => setReintentar(null) },
            )
          }
          alCerrar={() => setReintentar(null)}
        />
      )}
    </div>
  );
}

// ================================================================ XML al cliente (0117)

interface EnvioXml {
  id: string;
  estado: string;
  para: string | null;
  origen_correo: string | null;
  remitente: string | null;
  con_pdf: boolean;
  referencia: string | null;
  error: string | null;
  comentario: string | null;
  registrado_por: string;
  registrado_en: string;
}

const ORIGEN_CORREO: Record<string, string> = {
  SII: 'listado del SII',
  FICHA: 'ficha del cliente',
  MANUAL: 'escrito a mano',
};

/**
 * El XML de una factura o nota al correo de intercambio del cliente: cada envío (a quién, de dónde salió
 * el correo, quién lo pidió) y el botón para reenviarlo, al mismo correo o a otro. Lo manda el worker.
 */
export function XmlAlCliente({
  documento,
  puedeEnviar,
  alCerrar,
}: {
  documento: DocumentoDte;
  puedeEnviar: boolean;
  alCerrar: () => void;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const accion = useAccionDte(empresaId);
  const [para, setPara] = useState('');
  const [comentario, setComentario] = useState('');
  const q = useQuery({
    queryKey: ['emp', empresaId, 'dte-admin', 'intercambio', documento.id],
    queryFn: () =>
      api.get<{
        aplica: boolean;
        motivoNoAplica: string | null;
        correoSugerido: string | null;
        envios: EnvioXml[];
      }>(`/dte/documentos/${documento.id}/intercambio`),
    refetchInterval: (c) => (c.state.data?.envios[0]?.estado === 'SOLICITADO' ? 5_000 : false),
  });
  const listado = useQuery({
    queryKey: ['emp', empresaId, 'dte-admin', 'intercambio-listado'],
    queryFn: () =>
      api.get<{
        correcta: { fin: string; filas_archivo: number; filas_guardadas: number } | null;
        ultimoError: { fin: string; error: string } | null;
        contribuyentes: number;
      }>('/dte/intercambio/listado', { query: { empresaId } }),
  });
  const pendiente = q.data?.envios[0]?.estado === 'SOLICITADO';
  return (
    <Modal
      titulo={`XML al cliente · ${nombreTipo(documento.tipo_dte)} ${documento.folio ?? ''}`}
      alCerrar={alCerrar}
      ancho="ancho"
    >
      <fieldset className="recuadro-ficha" aria-label="Envíos del XML">
        <legend>Envíos</legend>
        <table className="grilla compacta" aria-label="Envíos del XML al cliente">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Estado</th>
              <th>Para</th>
              <th>Correo de</th>
              <th>PDF</th>
              <th>Pedido por</th>
              <th>Detalle</th>
            </tr>
          </thead>
          <tbody>
            {(q.data?.envios ?? []).map((e) => (
              <tr key={e.id}>
                <td>{fechaHora(e.registrado_en)}</td>
                <td>{ROTULO_INTERCAMBIO[e.estado] ?? e.estado}</td>
                <td>{e.para ?? '—'}</td>
                <td>{e.origen_correo ? ORIGEN_CORREO[e.origen_correo] : ''}</td>
                <td>{e.estado === 'ENVIADO' ? (e.con_pdf ? 'sí' : 'no') : ''}</td>
                <td>{e.registrado_por.startsWith('sistema:') ? 'automático' : e.registrado_por}</td>
                <td>{[e.comentario, e.error].filter(Boolean).join(' · ')}</td>
              </tr>
            ))}
            {!q.data?.envios.length && (
              <tr>
                <td colSpan={7} className="grilla-vacia">
                  {q.isLoading ? 'Cargando…' : 'Todavía no se ha enviado'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </fieldset>
      <fieldset className="recuadro-ficha" aria-label="Reenviar el XML">
        <legend>Reenviar</legend>
        <p>
          Correo de intercambio del cliente:{' '}
          <strong>{q.data?.correoSugerido ?? 'no tiene (ni en el SII ni en su ficha)'}</strong>
        </p>
        <label className="campo">
          <span className="etiqueta">Enviar a (vacío = el correo de intercambio)</span>
          <input
            aria-label="Enviar a"
            placeholder="correo@cliente.cl, otro@cliente.cl"
            value={para}
            disabled={!puedeEnviar}
            onChange={(e) => setPara(e.target.value)}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Comentario (opcional)</span>
          <input
            aria-label="Comentario"
            value={comentario}
            disabled={!puedeEnviar}
            onChange={(e) => setComentario(e.target.value)}
          />
        </label>
        {!puedeEnviar && <p>Reenviar requiere el permiso «dte.intercambio.enviar».</p>}
        {q.data && !q.data.aplica && <p>{q.data.motivoNoAplica}</p>}
        {pendiente && <p>Pedido: sale en menos de un minuto.</p>}
      </fieldset>
      <p>
        Listado de contribuyentes del SII:{' '}
        {listado.data?.correcta
          ? `actualizado ${fechaHora(listado.data.correcta.fin)} (${listado.data.contribuyentes} clientes registrados)`
          : 'todavía no se descarga'}
        {listado.data?.ultimoError ? ` · último error: ${listado.data.ultimoError.error}` : ''}
      </p>
      {(q.isError || accion.isError) && <PanelError error={q.error ?? accion.error} />}
      <div className="acciones-modal">
        <button type="button" onClick={alCerrar}>
          Cerrar
        </button>
        <button
          type="button"
          className="primario"
          disabled={!puedeEnviar || !q.data?.aplica || pendiente || accion.isPending}
          onClick={() =>
            accion.mutate(
              {
                ruta: `/dte/documentos/${documento.id}/intercambio`,
                cuerpo: {
                  ...(para.trim() ? { para: para.trim() } : {}),
                  ...(comentario.trim() ? { comentario: comentario.trim() } : {}),
                },
              },
              {
                onSuccess: () => {
                  setPara('');
                  setComentario('');
                  void q.refetch();
                },
              },
            )
          }
        >
          {para.trim() ? 'Enviar a ese correo' : 'Reenviar'}
        </button>
      </div>
    </Modal>
  );
}
